import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Download, Send, CheckCircle, Package, Truck, CreditCard, RefreshCw, Palette, Upload, Move, Type, Edit, ShoppingBag, Trash2, Wand2, Check, MessageSquare, Printer, Eye, X, Link as LinkIcon, ExternalLink, Sparkles, History, ChevronDown, ChevronRight, Calculator, Copy, DollarSign } from 'lucide-react';
import { AuthContext } from '../App';
import AppHeader from '../components/AppHeader';
import LogoPlacementEditor from '../components/LogoPlacementEditor';
import TextOverlayEditor from '../components/TextOverlayEditor';
import ProductEditor from '../components/ProductEditor';
import ProductMockupCard from '../components/ProductMockupCard';
import PrintSpecificationsEditor from '../components/PrintSpecificationsEditor';
import PaymentsSection from '../components/PaymentsSection';
import MockupApprovalTimeline from '../components/MockupApprovalTimeline';
import ProductDisplayCard, { groupItemsByMethod, MethodSectionHeader } from '../components/ProductDisplayCard';
import EditProductModal from '../components/EditProductModal';
import PickupReceipt from '../components/PickupReceipt';
import EmailActivityPanel from '../components/EmailActivityPanel';
import AdminPaymentModal from '../components/AdminPaymentModal';

const ProposalView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [proposal, setProposal] = useState(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [pickupOpen, setPickupOpen] = useState(false);
  const [showAdminPayment, setShowAdminPayment] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [showChangeHistory, setShowChangeHistory] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  
  // Mockup editor state
  const [showMockupEditor, setShowMockupEditor] = useState(false);
  const [selectedGarmentType, setSelectedGarmentType] = useState('t-shirt');
  const [selectedColor, setSelectedColor] = useState('');
  const [regeneratingMockup, setRegeneratingMockup] = useState(false);
  const [newLogoFile, setNewLogoFile] = useState(null);
  const [newLogoPreview, setNewLogoPreview] = useState(null);
  
  // Logo placement editor state
  const [showLogoPlacement, setShowLogoPlacement] = useState(false);
  const [selectedView, setSelectedView] = useState('front');
  const [applyingLogo, setApplyingLogo] = useState(false);
  
  // Text overlay editor state
  const [showTextEditor, setShowTextEditor] = useState(false);
  const [applyingText, setApplyingText] = useState(false);
  
  // Product editor state
  const [showProductEditor, setShowProductEditor] = useState(false);
  
  // Individual item editing state
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [editingItemData, setEditingItemData] = useState(null);
  // New unified Edit Product modal — InkSoft-style single dialog for color,
  // print, setup, and per-size price/qty edits.
  const [modalItemIndex, setModalItemIndex] = useState(null);
  const [savingItem, setSavingItem] = useState(false);
  const [uploadingItemDesign, setUploadingItemDesign] = useState(null);
  
  // Tax exempt state
  const [updatingTax, setUpdatingTax] = useState(false);
  const [uploadingCertificate, setUploadingCertificate] = useState(false);
  const [showCertificateUpload, setShowCertificateUpload] = useState(false);
  const [certificateFile, setCertificateFile] = useState(null);
  const [certificateNumber, setCertificateNumber] = useState('');
  const [certificateExpiry, setCertificateExpiry] = useState('');

  // Production pack preview modal state
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    fetchProposal();
    fetchOrder();
  }, [id]);

  const fetchProposal = async () => {
    try {
      const response = await axios.get(`/proposals/${id}`);
      setProposal(response.data);
    } catch (error) {
      toast.error('Failed to load proposal');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`/orders/by-proposal/${id}`);
      setOrder(response.data);
    } catch (error) {
      // Order doesn't exist yet, that's fine
      setOrder(null);
    }
  };

  const createOrder = async () => {
    setUpdating(true);
    try {
      // Determine print method from print_details or first item
      const printMethod = proposal.print_details?.method || proposal.items?.[0]?.print_method || 'screen_print';
      const response = await axios.post('/orders', {
        proposal_id: id,
        ordered_by: user?.name || 'Unknown',
        order_type: printMethod === 'embroidery' ? 'EMB' : 'SP'
      });
      setOrder(response.data);
      toast.success('Order created successfully! Go to Order Tracking to manage it.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    } finally {
      setUpdating(false);
    }
  };

  const updateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      await axios.put(`/proposals/${id}/status?status=${newStatus}`);
      setProposal({ ...proposal, status: newStatus });
      toast.success('Status updated successfully');
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const toggleTaxExempt = async () => {
    setUpdatingTax(true);
    try {
      const newTaxExempt = !proposal.tax_exempt;
      const response = await axios.patch(`/proposals/${id}/tax-exempt`, {
        tax_exempt: newTaxExempt
      });
      setProposal(response.data);
      toast.success(newTaxExempt ? 'Tax exemption applied' : 'Tax exemption removed');
      // Show certificate upload prompt when marking as tax exempt
      if (newTaxExempt && !response.data.tax_certificate) {
        setShowCertificateUpload(true);
      }
    } catch (error) {
      toast.error('Failed to update tax status');
    } finally {
      setUpdatingTax(false);
    }
  };

  const handleCertificateFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type (PDF, PNG, JPG)
      const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please upload a PDF or image file (PNG, JPG)');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setCertificateFile(file);
    }
  };

  const uploadTaxCertificate = async () => {
    if (!certificateFile) {
      toast.error('Please select a certificate file');
      return;
    }
    
    setUploadingCertificate(true);
    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result;
        const response = await axios.post(`/proposals/${id}/tax-certificate`, {
          certificate_base64: base64,
          filename: certificateFile.name,
          certificate_number: certificateNumber || null,
          expiry_date: certificateExpiry || null
        });
        setProposal(response.data);
        toast.success('Tax certificate uploaded successfully');
        setShowCertificateUpload(false);
        setCertificateFile(null);
        setCertificateNumber('');
        setCertificateExpiry('');
        setUploadingCertificate(false);
      };
      reader.readAsDataURL(certificateFile);
    } catch (error) {
      toast.error('Failed to upload certificate');
      setUploadingCertificate(false);
    }
  };

  const deleteTaxCertificate = async () => {
    if (!window.confirm('Are you sure you want to remove the tax certificate?')) {
      return;
    }
    try {
      const response = await axios.delete(`/proposals/${id}/tax-certificate`);
      setProposal(response.data);
      toast.success('Tax certificate removed');
    } catch (error) {
      toast.error('Failed to remove certificate');
    }
  };

  // Individual item editing functions
  const startEditingItem = (index) => {
    const item = proposal.items[index];
    setEditingItemIndex(index);
    setEditingItemData({
      ...item,
      sizes: { ...item.sizes }
    });
  };

  const cancelEditingItem = () => {
    setEditingItemIndex(null);
    setEditingItemData(null);
  };

  const updateItemSize = (size, value) => {
    const numValue = parseInt(value) || 0;
    const newSizes = { ...editingItemData.sizes, [size]: numValue };
    const newQuantity = Object.values(newSizes).reduce((sum, qty) => sum + qty, 0);
    setEditingItemData({
      ...editingItemData,
      sizes: newSizes,
      quantity: newQuantity
    });
  };

  const updateItemPrice = (field, value) => {
    setEditingItemData({
      ...editingItemData,
      [field]: parseFloat(value) || 0
    });
  };

  const saveItemChanges = async () => {
    setSavingItem(true);
    try {
      // Update the items array with the edited item
      const updatedItems = [...proposal.items];
      updatedItems[editingItemIndex] = editingItemData;
      
      // Size-aware line total: per-size overrides first, otherwise
      // base markup_price + auto-upcharge (2XL +$1.25, 3XL +$2.50, etc.)
      const UPCHARGES = { '2XL': 1.25, '3XL': 2.50, '4XL': 3.75, '5XL': 5.00 };
      const lineTotalFor = (item) => {
        const base = Number(item.markup_price) || Number(item.garment_cost) || 0;
        const overrides = item.size_price_overrides || {};
        const sizes = item.sizes || {};
        const sizeEntries = Object.entries(sizes).filter(([, q]) => Number(q || 0) > 0);
        if (sizeEntries.length === 0) {
          return base * (Number(item.quantity) || 0);
        }
        return sizeEntries.reduce((sum, [s, q]) => {
          const key = String(s).toUpperCase();
          const override = overrides[key] ?? overrides[s];
          const per = (override !== undefined && override !== null && override !== '' && Number.isFinite(Number(override)))
            ? Number(override)
            : base + (UPCHARGES[key] || 0);
          return sum + per * Number(q || 0);
        }, 0);
      };
      const shirtTotal = updatedItems.reduce((sum, item) => sum + lineTotalFor(item), 0);
      
      // Keep existing print totals
      const response = await axios.patch(`/proposals/${id}/items`, {
        items: updatedItems,
        shirt_total: shirtTotal,
        print_total: proposal.print_total,
        setup_fees: proposal.setup_fees,
        additional_fees: proposal.additional_fees
      });
      
      setProposal(response.data);
      setEditingItemIndex(null);
      setEditingItemData(null);
      toast.success('Product updated successfully');
    } catch (error) {
      toast.error('Failed to update product');
    } finally {
      setSavingItem(false);
    }
  };

  const removeItem = async (index) => {
    if (!window.confirm('Remove this product from the order?')) return;
    
    try {
      const updatedItems = proposal.items.filter((_, i) => i !== index);
      
      // Recalculate totals — same fallback as save flow
      const shirtTotal = updatedItems.reduce((sum, item) => {
        const perUnit = Number(item.markup_price) || Number(item.garment_cost) || 0;
        return sum + perUnit * (Number(item.quantity) || 0);
      }, 0);
      
      const response = await axios.patch(`/proposals/${id}/items`, {
        items: updatedItems,
        shirt_total: shirtTotal,
        print_total: proposal.print_total,
        setup_fees: proposal.setup_fees,
        additional_fees: proposal.additional_fees
      });
      
      setProposal(response.data);
      toast.success('Product removed');
    } catch (error) {
      toast.error('Failed to remove product');
    }
  };

  // One-click "Recalculate Totals" — re-derives shirt_total, print_total,
  // subtotal, tax, and grand total from the line items. Fixes proposals
  // where the rolled-up totals don't match what each line item displays.
  const recalculateTotals = async () => {
    if (!window.confirm(
      'Recalculate totals from the line items?\n\n' +
      'This re-derives Garments, Decoration, Subtotal, Tax, and Grand Total ' +
      'from each item\'s per-unit prices. Setup fees and tax-exempt status ' +
      'are preserved. An audit entry will be added to the Change History.'
    )) return;
    setRecalculating(true);
    try {
      const { data } = await axios.post(`/proposals/${id}/recalculate-totals`);
      if (data.changed) {
        const delta = (Number(data.new_total) - Number(data.old_total)).toFixed(2);
        toast.success(
          `Recalculated: $${Number(data.old_total).toFixed(2)} → $${Number(data.new_total).toFixed(2)} (${delta >= 0 ? '+' : ''}${delta})`
        );
        setProposal(data.proposal);
      } else {
        toast.info('Totals already match — nothing to repair.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Recalculate failed');
    } finally {
      setRecalculating(false);
    }
  };

  // Upload design file for a specific item
  const handleItemDesignUpload = async (itemIndex, file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, or PDF file');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    setUploadingItemDesign(itemIndex);
    
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result;
        
        // Update the items array with the design
        const updatedItems = [...proposal.items];
        updatedItems[itemIndex] = {
          ...updatedItems[itemIndex],
          design_file: base64,
          design_filename: file.name,
          design_type: file.type
        };
        
        const response = await axios.patch(`/proposals/${id}/items`, {
          items: updatedItems,
          shirt_total: proposal.shirt_total,
          print_total: proposal.print_total,
          setup_fees: proposal.setup_fees,
          additional_fees: proposal.additional_fees
        });
        
        setProposal(response.data);
        toast.success(`Design uploaded for Item ${itemIndex + 1}`);
        setUploadingItemDesign(null);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload design');
      setUploadingItemDesign(null);
    }
  };

  const removeItemDesign = async (itemIndex) => {
    if (!window.confirm('Remove the design file from this item?')) return;
    
    try {
      const updatedItems = [...proposal.items];
      delete updatedItems[itemIndex].design_file;
      delete updatedItems[itemIndex].design_filename;
      delete updatedItems[itemIndex].design_type;
      
      const response = await axios.patch(`/proposals/${id}/items`, {
        items: updatedItems,
        shirt_total: proposal.shirt_total,
        print_total: proposal.print_total,
        setup_fees: proposal.setup_fees,
        additional_fees: proposal.additional_fees
      });
      
      setProposal(response.data);
      toast.success('Design removed');
    } catch (error) {
      toast.error('Failed to remove design');
    }
  };

  const generateMockups = async () => {
    setUpdating(true);
    try {
      await axios.post(`/proposals/${id}/generate-mockups`);
      toast.success('Shirt mockups generated successfully!');
      fetchProposal(); // Refresh to show mockups
    } catch (error) {
      toast.error('Failed to generate mockups');
    } finally {
      setUpdating(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewLogoFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        setNewLogoPreview(base64);
      };
      reader.readAsDataURL(file);
    }
  };

  const regenerateMockups = async () => {
    setRegeneratingMockup(true);
    try {
      const requestData = {
        garment_type: selectedGarmentType,
        shirt_color: (selectedColor && selectedColor !== 'keep-current') ? selectedColor : undefined,
        logo_base64: newLogoPreview || undefined
      };
      
      await axios.post(`/proposals/${id}/regenerate-mockups`, requestData);
      toast.success(`Mockups regenerated as ${selectedGarmentType}!`);
      fetchProposal();
      setShowMockupEditor(false);
      setNewLogoFile(null);
      setNewLogoPreview(null);
    } catch (error) {
      toast.error('Failed to regenerate mockups');
      console.error(error);
    } finally {
      setRegeneratingMockup(false);
    }
  };

  const applyLogoPosition = async (position) => {
    if (!newLogoPreview) {
      toast.error('Please upload a logo first');
      return;
    }
    
    setApplyingLogo(true);
    try {
      const requestData = {
        logo_base64: newLogoPreview,
        view: selectedView,
        x: position.x,
        y: position.y,
        width: position.width,
        height: position.height,
        rotation: position.rotation || 0
      };
      
      await axios.post(`/proposals/${id}/apply-logo`, requestData);
      toast.success('Logo applied successfully!');
      fetchProposal();
      setShowLogoPlacement(false);
      setNewLogoFile(null);
      setNewLogoPreview(null);
    } catch (error) {
      toast.error('Failed to apply logo');
      console.error(error);
    } finally {
      setApplyingLogo(false);
    }
  };

  const openLogoPlacement = (view) => {
    setSelectedView(view);
    setShowLogoPlacement(true);
    setShowTextEditor(false);
  };

  const openTextEditor = (view) => {
    setSelectedView(view);
    setShowTextEditor(true);
    setShowLogoPlacement(false);
  };

  const applyTextOverlay = async (textConfig) => {
    setApplyingText(true);
    try {
      const requestData = {
        text: textConfig.text,
        view: selectedView,
        font_family: textConfig.fontFamily,
        font_size: textConfig.fontSize,
        text_color: textConfig.textColor,
        stroke_color: textConfig.strokeColor,
        has_stroke: textConfig.hasStroke,
        x: textConfig.position.x,
        y: textConfig.position.y,
        rotation: textConfig.position.rotation || 0
      };
      
      await axios.post(`/proposals/${id}/apply-text`, requestData);
      toast.success('Text applied successfully!');
      fetchProposal();
      setShowTextEditor(false);
    } catch (error) {
      toast.error('Failed to apply text');
      console.error(error);
    } finally {
      setApplyingText(false);
    }
  };

  const sendEmail = async () => {
    setUpdating(true);
    try {
      const { data } = await axios.post(`/proposals/${id}/send-email`);
      if (data?.paused) {
        toast.warning(data.message || 'Emails are paused (kill switch). Audit row recorded.');
      } else {
        toast.success('Proposal email sent successfully!');
      }
      fetchProposal(); // Refresh to show updated status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email. Please check SMTP configuration in backend/.env');
    } finally {
      setUpdating(false);
    }
  };

  // Save products changes
  const handleSaveProducts = async (newItems, pricing) => {
    try {
      await axios.patch(`/proposals/${id}/items`, {
        items: newItems,
        quantity: newItems.reduce((sum, item) => sum + (item.quantity || 0), 0),
        shirt_total: pricing?.shirt_total,
        print_total: pricing?.print_total,
        setup_fees: pricing?.setup_fees,
        additional_fees: pricing?.additional_fees,
        total_amount: pricing?.total_amount
      });
      await fetchProposal();
      setShowProductEditor(false);
    } catch (error) {
      throw error;
    }
  };

  // Update item when mockup changes. The card uses index = -1 as a sentinel to
  // request a full proposal replace (e.g. after a re-price that changes proposal totals).
  const handleItemUpdate = async (itemIndex, updatedItem) => {
    if (itemIndex === -1 && updatedItem && updatedItem.id) {
      setProposal(updatedItem);
      return;
    }
    const newItems = [...proposal.items];
    newItems[itemIndex] = updatedItem;
    setProposal({ ...proposal, items: newItems });
  };

  const approveAllMockups = async () => {
    if (!window.confirm('Mark every mockup on this proposal as approved on the customer\'s behalf?')) return;
    try {
      const r = await axios.post(`/proposals/${id}/admin-approve-all`);
      toast.success(r.data?.order_created ? 'All approved · order created' : 'All approved');
      // Refresh proposal so the new approval pills show up.
      try {
        const ref = await axios.get(`/proposals/${id}`);
        setProposal(ref.data);
      } catch { /* non-fatal */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve all');
    }
  };

  const downloadPDF = async () => {
    try {
      const response = await axios.post(`/proposals/${id}/pdf`);
      const pdfBase64 = response.data.pdf;
      
      // Convert base64 to blob and download
      const byteCharacters = atob(pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${proposal.proposal_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  const downloadProductionPack = async () => {
    try {
      toast.message('Building production pack…');
      const res = await axios.get(`/proposals/${id}/production-pack.pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `production-pack-${proposal.proposal_number || id}.pdf`;
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); document.body.removeChild(a);
      toast.success('Production pack downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to build production pack');
    }
  };

  const previewProductionPack = async () => {
    try {
      setLoadingPreview(true);
      toast.message('Building production pack preview…');
      const res = await axios.get(`/proposals/${id}/production-pack.pdf?inline=1`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPreviewPdfUrl(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to build production pack preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const closePreview = () => {
    if (previewPdfUrl) {
      window.URL.revokeObjectURL(previewPdfUrl);
    }
    setPreviewPdfUrl(null);
  };

  const printProductionPack = () => {
    const iframe = document.getElementById('production-pack-preview-iframe');
    if (iframe && iframe.contentWindow) {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (e) {
        toast.error('Print failed — try the browser print button in the PDF viewer toolbar');
      }
    }
  };

  const downloadFromPreview = () => {
    if (!previewPdfUrl) return;
    const a = document.createElement('a');
    a.href = previewPdfUrl;
    a.download = `production-pack-${proposal.proposal_number || id}.pdf`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
  };

  // Public share link — same URL the client uses to view + accept/pay the
  // proposal. Includes the full item review, mockup thumbnails, decoration
  // specs, timestamped production notes, and the FIU/payment CTAs.
  const copyShareLink = async () => {
    const { data } = await axios.get(`/proposals/${id}/share-link`);
    const shareUrl = data.url;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const ta = document.createElement('textarea');
        ta.value = shareUrl; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta);
      }
      toast.success('Client link copied to clipboard');
    } catch {
      // Fallback — show the URL in a prompt the user can copy manually
      window.prompt('Copy this client link:', shareUrl);
    }
  };

  const openClientPreview = async () => {
    const { data } = await axios.get(`/proposals/${id}/share-link`);
    window.open(data.url, '_blank', 'noopener');
  };

  const handlePayNow = async () => {
    // Open the customer-facing Inspyre payment page in a new tab.
    // (Stripe checkout was retired; payments now go through /pay/{id}.)
    const url = (await axios.get(`/proposals/${id}/share-link`)).data.url;
    try {
      // Copy the link to clipboard for convenience
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        toast.success('Payment link copied — opening checkout page');
      }
    } catch (e) {
      // Clipboard might not be available; ignore
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }

  if (!proposal) {
    return <div>Proposal not found</div>;
  }

  return (
    <div className="dashboard" data-testid="proposal-view">
      <AppHeader />

      <main className="dashboard-content">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: '700', margin: 0 }} data-testid="proposal-title">
              {proposal.order_number ? `Order #${proposal.order_number}` : proposal.proposal_number}
            </h1>
            {proposal.order_number && (
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4, letterSpacing: 0.3 }}>
                Proposal ref: {proposal.proposal_number}
              </div>
            )}
            {!proposal.order_number && user?.role === 'admin' && (
              <button
                onClick={async () => {
                  if (!window.confirm('Assign a sequential Order # to this proposal? This is normally automatic when you click Send Email, but can be set manually here.')) return;
                  try {
                    const { data } = await axios.post(`/proposals/${id}/assign-order-number`);
                    toast.success(`Order #${data.order_number} assigned`);
                    fetchProposal();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Failed to assign order number');
                  }
                }}
                data-testid="assign-order-number-button"
                style={{
                  marginTop: 6, background: 'transparent', border: '1px dashed #0ea5e9',
                  color: '#0ea5e9', padding: '4px 10px', borderRadius: 6,
                  fontSize: 12, fontWeight: 500, cursor: 'pointer',
                }}
              >
                Assign Order #
              </button>
            )}
          </div>
        </div>

        <div style={{ background: 'white', borderRadius: '16px', padding: '32px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          {/* Header Actions */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '32px', paddingBottom: '24px', borderBottom: '2px solid #e2e8f0' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '600' }}>Status:</h2>
                <Select
                  value={proposal.status}
                  onValueChange={updateStatus}
                  disabled={updating}
                >
                  <SelectTrigger style={{ width: '150px' }} data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="ordered">Ordered</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="picked_up">Picked Up</SelectItem>
                  </SelectContent>
                </Select>
                {/* Mark Completed + Pickup Receipt CTAs.
                    Visible whenever the proposal has reached a billable state
                    (paid/ordered/completed) so the admin can flip it through
                    the pickup workflow with one click. */}
                {['paid', 'ordered', 'completed', 'picked_up'].includes(proposal.status) && (
                  <>
                    {proposal.status !== 'picked_up' && proposal.status !== 'completed' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          try {
                            await axios.post(`/proposals/${proposal.id}/mark-completed`);
                            toast.success('Marked as completed');
                            fetchProposal();
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || 'Could not mark completed');
                          }
                        }}
                        data-testid="mark-completed-btn"
                      >
                        <CheckCircle size={14} className="mr-1" />
                        Mark Completed
                      </Button>
                    )}
                    {proposal.status === 'completed' && !proposal.pickup_notified_at && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          try {
                            const r = await axios.post(`/proposals/${proposal.id}/notify-pickup`);
                            if (r.data.paused) {
                              toast.info('Emails are paused globally — pickup notification queued. Customer will be notified once emails resume.');
                            } else if (r.data.success) {
                              toast.success('Pickup-ready email sent to customer');
                            } else {
                              toast.warning('Email send failed — try again or notify the customer manually');
                            }
                            fetchProposal();
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || 'Could not notify customer');
                          }
                        }}
                        data-testid="notify-pickup-btn"
                      >
                        <Send size={14} className="mr-1" />
                        Notify Customer (Pickup Ready)
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant={proposal.pickup ? 'outline' : 'default'}
                      onClick={() => setPickupOpen(true)}
                      data-testid="open-pickup-receipt-btn"
                    >
                      <Package size={14} className="mr-1" />
                      {proposal.pickup ? 'View Pickup Receipt' : 'Open Pickup Receipt'}
                    </Button>
                  </>
                )}
              </div>
              {/* Pickup summary badge — shown after a signature is captured */}
              {proposal.pickup && (
                <div
                  className="mt-2 p-2 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900 text-xs"
                  data-testid="pickup-summary"
                >
                  ✅ Picked up by <strong>{proposal.pickup.picked_up_by_name}</strong>
                  {proposal.pickup.picked_up_at && (
                    <> on <strong>{new Date(proposal.pickup.picked_up_at).toLocaleString()}</strong></>
                  )}
                  {proposal.pickup.released_by && <> · released by {proposal.pickup.released_by}</>}
                </div>
              )}

              {/* Auto follow-up control. Backend already gates day1/3/5
                  follow-ups on `sent_at` (= admin clicked Send Email),
                  but this lets you opt a specific proposal out of any
                  further automated chase emails without changing env vars. */}
              {(proposal.status === 'sent' || proposal.status === 'paid') && (
                <label
                  className="mt-2 flex items-center gap-2 text-xs text-gray-600 cursor-pointer"
                  data-testid="auto-followups-toggle"
                >
                  <input
                    type="checkbox"
                    checked={!proposal.auto_followups_disabled}
                    onChange={async (e) => {
                      const enable = e.target.checked;
                      try {
                        await axios.post(`/proposals/${proposal.id}/${enable ? 'enable' : 'disable'}-followups`);
                        toast.success(enable ? 'Auto follow-ups enabled' : 'Auto follow-ups disabled');
                        fetchProposal();
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Could not toggle follow-ups');
                      }
                    }}
                  />
                  <span>
                    Send automated day-1 / day-3 / day-5 follow-ups after I click <strong>Send Email</strong>
                  </span>
                </label>
              )}
              <p style={{ color: '#718096', fontSize: '14px' }} data-testid="proposal-date">
                Created: {(() => {
                  try {
                    return new Date(proposal.created_at).toLocaleString('en-US', {
                      month: 'short', day: 'numeric', year: 'numeric',
                      hour: 'numeric', minute: '2-digit',
                    });
                  } catch (e) {
                    return new Date(proposal.created_at).toLocaleDateString();
                  }
                })()}
                {proposal.created_by_name && (
                  <>
                    {' '}by{' '}
                    <span
                      data-testid="proposal-created-by"
                      title={proposal.created_by_email || undefined}
                      style={{ fontWeight: 600, color: '#475569' }}
                    >
                      {proposal.created_by_name}
                    </span>
                  </>
                )}
              </p>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <Button variant="outline" onClick={() => navigate(`/proposal/${id}/edit`)} data-testid="edit-proposal-button">
                Edit
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/ai-assistant?edit=${proposal.proposal_number}`)}
                data-testid="edit-with-ai-button"
                title="Open the AI Assistant pre-loaded with this proposal — just describe the change in chat"
                style={{ borderColor: '#7c3aed', color: '#7c3aed' }}
              >
                <Sparkles size={16} style={{ marginRight: 6 }} /> Edit with AI
              </Button>
              <Button
                variant="outline"
                onClick={recalculateTotals}
                disabled={recalculating}
                data-testid="recalculate-totals-button"
                title="Re-derive Garments, Decoration, Subtotal, Tax, and Grand Total from the line items. Fixes proposals where the rolled-up totals don't match."
                style={{ borderColor: '#0ea5e9', color: '#0ea5e9' }}
              >
                <Calculator size={16} style={{ marginRight: 6 }} />
                {recalculating ? 'Recalculating…' : 'Recalculate Totals'}
              </Button>
              <Button onClick={downloadPDF} data-testid="download-pdf-button">
                <Download size={18} /> Download PDF
              </Button>
              <Button variant="outline" onClick={downloadProductionPack}
                data-testid="download-production-pack-button"
                style={{ borderColor: '#7c3aed', color: '#7c3aed' }}>
                <Download size={18} /> Production Pack
              </Button>
              <Button variant="outline" onClick={previewProductionPack}
                disabled={loadingPreview}
                data-testid="preview-production-pack-button"
                style={{ borderColor: '#7c3aed', color: '#7c3aed' }}>
                <Eye size={18} /> {loadingPreview ? 'Loading…' : 'Preview / Print'}
              </Button>
              <Button variant="outline" onClick={copyShareLink}
                data-testid="copy-share-link-button"
                title="Copy the client-facing link (shows mockups, totals, accept + pay)"
                style={{ borderColor: '#0ea5e9', color: '#0ea5e9' }}>
                <LinkIcon size={18} /> Copy Client Link
              </Button>
              <Button variant="outline" onClick={openClientPreview}
                data-testid="open-client-preview-button"
                title="Open the page exactly as the client sees it (new tab)"
                style={{ borderColor: '#0ea5e9', color: '#0ea5e9' }}>
                <ExternalLink size={18} /> Preview as Client
              </Button>
              <Button variant="outline" onClick={sendEmail} disabled={updating} data-testid="send-email-button">
                <Send size={18} /> Send Email
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowAdminPayment(true)}
                data-testid="take-payment-button"
                title="Record an in-office payment (card, cash, check, or other)"
                style={{ borderColor: '#16a34a', color: '#16a34a' }}
              >
                <DollarSign size={18} /> Take Payment
              </Button>
              <Button
                variant="outline"
                data-testid="duplicate-proposal-button"
                disabled={updating}
                title="Create a fresh draft from this proposal — same items, customer, and notes; new proposal #, no payments or order #."
                onClick={async () => {
                  const label = proposal.proposal_number || proposal.order_number || 'this proposal';
                  if (!window.confirm(`Duplicate ${label}?\n\nA new DRAFT will be created with the same customer, items, sizes, decoration, and production notes. Payments, order #, sent date, and approvals will NOT be carried over.`)) return;
                  try {
                    setUpdating(true);
                    const { data } = await axios.post(`/proposals/${id}/duplicate`);
                    toast.success(`Duplicated → ${data.proposal_number}`);
                    navigate(`/proposal/${data.id}`);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Failed to duplicate proposal');
                  } finally {
                    setUpdating(false);
                  }
                }}
                style={{ borderColor: '#8b5cf6', color: '#7c3aed' }}>
                <Copy size={18} /> Duplicate
              </Button>
              {proposal.status !== 'paid' && (
                <Button 
                  onClick={handlePayNow} 
                  disabled={processingPayment}
                  style={{ background: '#635bff' }}
                  data-testid="pay-now-button"
                >
                  <CreditCard size={18} /> {processingPayment ? 'Processing...' : 'Pay Now'}
                </Button>
              )}
              {proposal.status === 'paid' && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  padding: '8px 16px', 
                  background: '#dcfce7', 
                  borderRadius: '8px',
                  color: '#16a34a',
                  fontWeight: '600'
                }}>
                  <CheckCircle size={18} /> Paid
                </div>
              )}
              {!order ? (
                <Button 
                  onClick={createOrder} 
                  disabled={updating}
                  style={{ background: '#22c55e' }}
                  data-testid="create-order-button"
                >
                  <Package size={18} /> Create Order
                </Button>
              ) : (
                <Button 
                  onClick={() => navigate('/orders')}
                  style={{ background: '#8b5cf6' }}
                  data-testid="view-order-button"
                >
                  <Truck size={18} /> View in Order Tracking
                </Button>
              )}
            </div>
          </div>

          {/* Email Activity audit log — every send attempt (sent / failed / paused) plus resend */}
          <EmailActivityPanel proposalId={id} />

          {/* Order Tracking Status - Show if order exists */}
          {order && (
            <div style={{ 
              marginBottom: '32px', 
              background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)', 
              padding: '24px', 
              borderRadius: '12px',
              color: 'white'
            }} data-testid="order-status-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <Truck size={24} />
                <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>Order Tracking</h3>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
                <div>
                  <p style={{ fontSize: '12px', opacity: 0.8 }}>PO Number</p>
                  <p style={{ fontWeight: '600' }}>{order.po_number || '-'}</p>
                </div>
                <div>
                  <p style={{ fontSize: '12px', opacity: 0.8 }}>Status</p>
                  <p style={{ fontWeight: '600' }}>{order.status}</p>
                </div>
                <div>
                  <p style={{ fontSize: '12px', opacity: 0.8 }}>ETA</p>
                  <p style={{ fontWeight: '600' }}>{order.eta ? new Date(order.eta).toLocaleDateString() : '-'}</p>
                </div>
                <div>
                  <p style={{ fontSize: '12px', opacity: 0.8 }}>Tracking #</p>
                  <p style={{ fontWeight: '600' }}>{order.tracking_number || '-'}</p>
                </div>
              </div>
            </div>
          )}

          {/* AI Edit History - collapsible */}
          {(proposal.ai_edit_log || []).length > 0 && (
            <div
              style={{ marginBottom: '24px', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}
              data-testid="ai-edit-history-panel"
            >
              <button
                onClick={() => setShowChangeHistory((v) => !v)}
                data-testid="ai-edit-history-toggle"
                style={{
                  width: '100%', background: '#faf5ff', border: 'none',
                  padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  cursor: 'pointer', textAlign: 'left',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <History size={18} color="#7c3aed" />
                  <span style={{ fontWeight: 600, color: '#581c87' }}>
                    Change History
                  </span>
                  <span style={{
                    background: '#7c3aed', color: '#fff',
                    fontSize: 11, fontWeight: 700,
                    padding: '2px 8px', borderRadius: 999,
                  }}>
                    {proposal.ai_edit_log.length}
                  </span>
                </span>
                {showChangeHistory ? <ChevronDown size={16} color="#7c3aed" /> : <ChevronRight size={16} color="#7c3aed" />}
              </button>
              {showChangeHistory && (
                <div style={{ padding: '4px 0', background: '#fff' }}>
                  {[...proposal.ai_edit_log].reverse().map((entry, i) => {
                    const at = entry.at ? new Date(entry.at) : null;
                    const delta = (entry.new_total || 0) - (entry.old_total || 0);
                    const arrow = delta > 0 ? '↑' : (delta < 0 ? '↓' : '·');
                    const deltaColor = delta > 0 ? '#dc2626' : (delta < 0 ? '#059669' : '#64748b');
                    return (
                      <div
                        key={i}
                        data-testid={`ai-edit-entry-${i}`}
                        style={{
                          padding: '12px 16px',
                          borderTop: '1px solid #f1f5f9',
                          display: 'grid',
                          gridTemplateColumns: '160px 1fr auto',
                          gap: 16,
                          alignItems: 'flex-start',
                        }}
                      >
                        <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
                          <div style={{ fontWeight: 600, color: '#334155' }}>
                            {at ? at.toLocaleDateString() : '—'}
                          </div>
                          <div>{at ? at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</div>
                          <div style={{ marginTop: 4, fontStyle: 'italic' }}>{entry.by || 'ai'}</div>
                          {entry.role && (
                            <div style={{
                              marginTop: 4,
                              display: 'inline-block',
                              fontSize: 10,
                              fontWeight: 700,
                              padding: '2px 6px',
                              borderRadius: 4,
                              background: entry.role === 'ai' ? '#ede9fe' : '#dbeafe',
                              color: entry.role === 'ai' ? '#6d28d9' : '#1e40af',
                              textTransform: 'uppercase',
                              letterSpacing: 0.5,
                            }}>
                              {entry.role === 'ai' ? 'AI' : entry.role}
                            </div>
                          )}
                        </div>
                        <div style={{ fontSize: 13, color: '#0f172a' }}>
                          {(entry.changes && entry.changes.length > 0) ? (
                            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
                              {entry.changes.map((c, ci) => (
                                <li key={ci}>{c}</li>
                              ))}
                            </ul>
                          ) : (
                            <div>{entry.text || '—'}</div>
                          )}
                        </div>
                        <div style={{ textAlign: 'right', fontSize: 13, whiteSpace: 'nowrap' }}>
                          <div style={{ color: '#64748b' }}>
                            ${Number(entry.old_total || 0).toFixed(2)} → <strong>${Number(entry.new_total || 0).toFixed(2)}</strong>
                          </div>
                          <div style={{ color: deltaColor, fontWeight: 600, marginTop: 2 }}>
                            {arrow} ${Math.abs(delta).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Customer Info */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>Customer Information</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
              <div>
                <p style={{ color: '#718096', fontSize: '14px', marginBottom: '4px' }}>Name</p>
                <p style={{ fontWeight: '500' }} data-testid="customer-name">{proposal.customer_name}</p>
              </div>
              <div>
                <p style={{ color: '#718096', fontSize: '14px', marginBottom: '4px' }}>Email</p>
                <p style={{ fontWeight: '500' }} data-testid="customer-email">{proposal.customer_email}</p>
              </div>
              <div>
                <p style={{ color: '#718096', fontSize: '14px', marginBottom: '4px' }}>Phone</p>
                <p style={{ fontWeight: '500' }} data-testid="customer-phone">{proposal.customer_phone || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Order Details */}
          <div style={{ marginBottom: '32px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600' }}>Order Details</h3>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowProductEditor(true)}
                data-testid="edit-products-btn"
              >
                <ShoppingBag size={16} className="mr-2" /> Edit Products
              </Button>
            </div>
            
            {/* Product Editor Modal */}
            {showProductEditor && (
              <div style={{ marginBottom: '24px' }}>
                <ProductEditor 
                  proposal={proposal}
                  onSave={handleSaveProducts}
                  onCancel={() => setShowProductEditor(false)}
                />
              </div>
            )}
            
            {!showProductEditor && (
              <div style={{ background: '#f7fafc', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <p><strong>Quantity:</strong> {proposal.quantity || proposal.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 'N/A'}</p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowProductEditor(true)}
                    style={{ fontSize: '12px' }}
                  >
                    <ShoppingBag className="w-3 h-3 mr-1" />
                    Add Product
                  </Button>
                </div>
                {proposal.items && groupItemsByMethod(proposal.items).map((group) => (
                  <div key={group.label} style={{ marginBottom: 20 }} data-testid={`method-group-${group.label.toLowerCase().replace(/\s+/g, '-')}`}>
                    {/* Method group header — only when there's >1 group, so single-method
                        proposals don't get a redundant banner */}
                    {groupItemsByMethod(proposal.items).length > 1 && (
                      <MethodSectionHeader
                        label={group.label}
                        itemCount={group.items.length}
                        totalPieces={group.totalPieces}
                        totalAmount={group.totalAmount}
                      />
                    )}
                    {group.items.map((item) => {
                      const idx = item._origIndex;
                      return (
                  <div key={idx} style={{ 
                    marginBottom: '12px', 
                    padding: '16px',
                    background: editingItemIndex === idx ? '#eef2ff' : (item.is_cpp ? '#fef3c7' : 'white'),
                    borderRadius: '8px',
                    border: editingItemIndex === idx ? '2px solid #667eea' : (item.is_cpp ? '1px solid #fbbf24' : '1px solid #e2e8f0')
                  }}>
                    {item.is_cpp && (
                      <span style={{ 
                        display: 'inline-block',
                        fontSize: '11px', 
                        fontWeight: '600', 
                        color: '#92400e', 
                        background: '#fef3c7', 
                        padding: '2px 6px', 
                        borderRadius: '4px',
                        marginBottom: '4px'
                      }}>
                        CPP - Customer Provided
                      </span>
                    )}
                    
                    {editingItemIndex === idx ? (
                      // Editing mode for this item
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <p style={{ fontWeight: '700', fontSize: '16px', color: '#667eea' }}>
                            Editing: {editingItemData.brand} - {editingItemData.style} ({editingItemData.color})
                          </p>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <Button size="sm" variant="outline" onClick={cancelEditingItem} disabled={savingItem}>
                              Cancel
                            </Button>
                            <Button size="sm" onClick={saveItemChanges} disabled={savingItem} style={{ background: '#22c55e' }}>
                              {savingItem ? 'Saving...' : 'Save Changes'}
                            </Button>
                          </div>
                        </div>
                        
                        {/* Garment swap — change brand, style, color, and style number */}
                        <div style={{ marginBottom: '16px', padding: '12px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                          <p style={{ fontSize: '13px', fontWeight: '600', marginBottom: '10px', color: '#0f172a' }}>
                            Garment Details
                          </p>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '10px' }}>
                            <div>
                              <label style={{ fontSize: '11px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>Brand</label>
                              <input
                                type="text"
                                value={editingItemData.brand || ''}
                                onChange={(e) => setEditingItemData({ ...editingItemData, brand: e.target.value })}
                                placeholder="e.g. Bella + Canvas"
                                data-testid="edit-brand-input"
                                style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px' }}
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: '11px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>Style #</label>
                              <input
                                type="text"
                                value={editingItemData.style_number || editingItemData.styleID || ''}
                                onChange={(e) => setEditingItemData({ ...editingItemData, style_number: e.target.value, styleID: e.target.value })}
                                placeholder="e.g. 3001"
                                data-testid="edit-style-number-input"
                                style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px' }}
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: '11px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>Style Name</label>
                              <input
                                type="text"
                                value={editingItemData.style || editingItemData.product_name || ''}
                                onChange={(e) => setEditingItemData({ ...editingItemData, style: e.target.value, product_name: e.target.value })}
                                placeholder="e.g. Unisex Jersey Tee"
                                data-testid="edit-style-name-input"
                                style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px' }}
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: '11px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>Color</label>
                              <input
                                type="text"
                                value={editingItemData.color || ''}
                                onChange={(e) => setEditingItemData({ ...editingItemData, color: e.target.value })}
                                placeholder="e.g. Navy"
                                data-testid="edit-color-input"
                                style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px' }}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Size editing — qty per size with optional per-size price override */}
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
                            <p style={{ fontSize: '13px', fontWeight: '600', margin: 0 }}>Sizes & Per-Size Pricing:</p>
                            <button
                              type="button"
                              data-testid="clear-size-overrides"
                              onClick={() => setEditingItemData({ ...editingItemData, size_price_overrides: {} })}
                              style={{ background: 'transparent', border: 'none', color: '#0ea5e9', fontSize: 11, cursor: 'pointer', padding: 0 }}
                            >
                              Reset all to base price + upcharge
                            </button>
                          </div>
                          <p style={{ fontSize: '11px', color: '#64748b', marginBottom: 8 }}>
                            Leave price blank to use Unit Price + auto-upcharge (2XL +$1.25, 3XL +$2.50, 4XL +$3.75, 5XL +$5.00). Type a price to override that size only.
                          </p>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                            {['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'].map(size => {
                              const overrides = editingItemData.size_price_overrides || {};
                              const overrideVal = overrides[size];
                              const upchargeMap = { '2XL': 1.25, '3XL': 2.50, '4XL': 3.75, '5XL': 5.00 };
                              const defaultPrice = (Number(editingItemData.markup_price) || 0) + (upchargeMap[size] || 0);
                              return (
                                <div key={size} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 70 }}>
                                  <label style={{ fontSize: '11px', fontWeight: '600', marginBottom: '2px' }}>{size}</label>
                                  <input
                                    type="number"
                                    min="0"
                                    value={editingItemData.sizes[size] || 0}
                                    onChange={(e) => updateItemSize(size, e.target.value)}
                                    data-testid={`size-qty-${size}`}
                                    style={{
                                      width: '60px', padding: '4px 6px', textAlign: 'center',
                                      border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px',
                                    }}
                                  />
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder={`$${defaultPrice.toFixed(2)}`}
                                    value={overrideVal !== undefined && overrideVal !== null ? overrideVal : ''}
                                    onChange={(e) => {
                                      const next = { ...(editingItemData.size_price_overrides || {}) };
                                      if (e.target.value === '') delete next[size];
                                      else next[size] = parseFloat(e.target.value);
                                      setEditingItemData({ ...editingItemData, size_price_overrides: next });
                                    }}
                                    data-testid={`size-price-${size}`}
                                    style={{
                                      width: '70px', padding: '4px 6px', textAlign: 'center',
                                      border: overrideVal !== undefined && overrideVal !== null ? '1px solid #0ea5e9' : '1px solid #e2e8f0',
                                      background: overrideVal !== undefined && overrideVal !== null ? '#f0f9ff' : '#fff',
                                      borderRadius: '4px', fontSize: '11px', marginTop: 4,
                                      color: overrideVal !== undefined && overrideVal !== null ? '#0c4a6e' : '#94a3b8',
                                    }}
                                  />
                                </div>
                              );
                            })}
                          </div>
                          <p style={{ fontSize: '12px', color: '#667eea', marginTop: '8px', fontWeight: '600' }}>
                            Total Qty: {editingItemData.quantity}
                          </p>
                        </div>
                        
                        {/* Price editing */}
                        <div style={{ display: 'flex', gap: '16px' }}>
                          <div>
                            <label style={{ fontSize: '12px', fontWeight: '600' }}>Unit Price ($)</label>
                            <input
                              type="number"
                              step="0.01"
                              value={editingItemData.markup_price || 0}
                              onChange={(e) => updateItemPrice('markup_price', e.target.value)}
                              style={{
                                width: '100px',
                                padding: '6px 8px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontSize: '13px',
                                marginTop: '4px'
                              }}
                            />
                          </div>
                          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                            <p style={{ fontSize: '14px', fontWeight: '600', color: '#22c55e' }}>
                              Line Total: ${(editingItemData.markup_price * editingItemData.quantity).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // View mode for this item — polished display card
                      <div>
                        <ProductDisplayCard
                          item={item}
                          isAdmin={user?.role === 'admin'}
                          onEdit={() => setModalItemIndex(idx)}
                          onRemove={() => removeItem(idx)}
                          onEditNotes={() => {
                            // The Notes Log lives in the per-item Mockup card
                            // further down the page. Scroll to it and try to
                            // expand the item card + focus the text input so
                            // the admin can type immediately.
                            const target = document.querySelector(`[data-testid="decoration-notes-${idx}"]`);
                            if (target) {
                              // If the parent mockup card has a collapsed state,
                              // click it once to expand. Mockup card header has
                              // data-testid="product-mockup-card-header-{idx}".
                              const header = document.querySelector(`[data-testid="product-mockup-card-header-${idx}"]`);
                              if (header && target.getBoundingClientRect().height < 50) {
                                header.click();
                              }
                              target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                              setTimeout(() => {
                                const input = target.querySelector('textarea, input[type="text"]');
                                if (input) input.focus();
                              }, 400);
                            } else {
                              // Fallback: scroll to mockups section
                              const mockups = document.querySelector('[data-testid="product-mockups-section"]');
                              if (mockups) mockups.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                          }}
                          onSetupFeeChange={async (newFee) => {
                            try {
                              const { data } = await axios.patch(
                                `/proposals/${id}/items/${idx}/setup-fee`,
                                { setup_fee: newFee },
                              );
                              setProposal(data.proposal || data);
                              toast.success(`Setup fee updated to $${Number(newFee).toFixed(2)}`);
                            } catch (e) {
                              toast.error(e.response?.data?.detail || 'Failed to update setup fee');
                            }
                          }}
                          onSizePriceChange={async (size, newPrice) => {
                            // Inline per-size override save: surgical update
                            // via the existing PATCH /items endpoint, then
                            // recalculate to refresh proposal totals.
                            try {
                              const updatedItems = proposal.items.map((it, ii) => {
                                if (ii !== idx) return it;
                                const next = { ...(it.size_price_overrides || {}) };
                                if (newPrice === null || newPrice === undefined) {
                                  delete next[size];
                                } else {
                                  next[size] = Number(newPrice);
                                }
                                return { ...it, size_price_overrides: next };
                              });
                              await axios.patch(`/proposals/${id}/items`, {
                                items: updatedItems,
                                shirt_total: proposal.shirt_total,
                                print_total: proposal.print_total,
                                setup_fees: proposal.setup_fees,
                                additional_fees: proposal.additional_fees,
                              });
                              const { data: recalc } = await axios.post(`/proposals/${id}/recalculate-totals`);
                              setProposal(recalc.proposal || recalc);
                              toast.success(`${size} price ${newPrice === null ? 'reset' : `set to $${Number(newPrice).toFixed(2)}`}`);
                            } catch (e) {
                              toast.error(e.response?.data?.detail || 'Failed to update size price');
                            }
                          }}

                          onPrintPriceChange={async (newPrice) => {
                            try {
                              const { data } = await axios.patch(
                                `/proposals/${id}/items/${idx}/print-cost`,
                                { print_cost: newPrice },
                              );
                              setProposal(data.proposal || data);
                              toast.success(`Print price set to $${Number(newPrice).toFixed(2)}/ea (matrix auto-pricing disabled for this item)`);
                            } catch (e) {
                              toast.error(e.response?.data?.detail || 'Failed to update print price');
                            }
                          }}

                          onColorsChange={async (newColors) => {
                            // Two-step flow: dry_run preview → confirm dialog → apply
                            try {
                              const { data: preview } = await axios.post(
                                `/proposals/${id}/items/${idx}/reprice-colors`,
                                { print_colors: newColors, dry_run: true },
                              );
                              const p = preview.preview || {};
                              const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
                              const ok = window.confirm(
                                `Re-price this item?\n\n` +
                                `Changing colors from ${p.old_colors} → ${p.new_colors} will update:\n\n` +
                                `• Print cost:        ${fmt(p.old_print_cost)}/ea → ${fmt(p.new_print_cost)}/ea\n` +
                                `• Item line total:   ${fmt(p.old_item_total)} → ${fmt(p.new_item_total)}\n` +
                                `• Proposal total:    ${fmt(p.old_proposal_total)} → ${fmt(p.new_proposal_total)}\n\n` +
                                `Setup fees are NOT changed automatically — adjust the Setup Fee field separately if needed.\n\n` +
                                `Proceed?`
                              );
                              if (!ok) return;
                              const { data } = await axios.post(
                                `/proposals/${id}/items/${idx}/reprice-colors`,
                                { print_colors: newColors, dry_run: false },
                              );
                              setProposal(data.proposal || data);
                              toast.success(`Re-priced: ${fmt(p.old_proposal_total)} → ${fmt(p.new_proposal_total)}`);
                            } catch (e) {
                              toast.error(e.response?.data?.detail || 'Failed to re-price');
                            }
                          }}
                          onLocationsChange={async (newLocations, removed) => {
                            // Single handler for both ADD and REMOVE of a
                            // print location. The card calls this with
                            // - removed = "Back" (label) when X is clicked
                            // - removed = null when the +Add menu is used
                            // Flow: initial confirm → dry_run price preview
                            // → final confirm → apply.
                            const isAdd = !removed;
                            const added = isAdd && newLocations.length > 0
                              ? newLocations[newLocations.length - 1]
                              : null;
                            try {
                              const initialMsg = isAdd
                                ? `Add "${added}" print to this item?\n\nThis will re-price the item from the matrix using ${newLocations.length} location(s). Setup fee is NOT changed automatically — adjust it separately if needed.`
                                : `Remove "${removed}" print from this item?\n\nThis will re-price the item from the matrix using the remaining ${newLocations.length} location(s).`;
                              const initialOk = window.confirm(initialMsg);
                              if (!initialOk) return;
                              const { data: preview } = await axios.post(
                                `/proposals/${id}/items/${idx}/reprice-locations`,
                                { print_locations: newLocations, dry_run: true },
                              );
                              const p = preview.preview || {};
                              const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
                              const ok = window.confirm(
                                `Confirm re-price?\n\n` +
                                `Locations: ${(p.old_locations || []).join(', ') || '(none)'} → ${(p.new_locations || []).join(', ')}\n\n` +
                                `• Print cost:        ${fmt(p.old_print_cost)}/ea → ${fmt(p.new_print_cost)}/ea\n` +
                                `• Item line total:   ${fmt(p.old_item_total)} → ${fmt(p.new_item_total)}\n` +
                                `• Proposal total:    ${fmt(p.old_proposal_total)} → ${fmt(p.new_proposal_total)}\n\n` +
                                `Setup fees are NOT changed automatically — adjust the Setup Fee separately if needed.\n\n` +
                                `Proceed?`
                              );
                              if (!ok) return;
                              const { data } = await axios.post(
                                `/proposals/${id}/items/${idx}/reprice-locations`,
                                { print_locations: newLocations, dry_run: false },
                              );
                              setProposal(data.proposal || data);
                              const verb = isAdd ? `Added "${added}"` : `Removed "${removed}"`;
                              toast.success(`${verb} — re-priced ${fmt(p.old_proposal_total)} → ${fmt(p.new_proposal_total)}`);
                            } catch (e) {
                              toast.error(e.response?.data?.detail || 'Failed to update locations');
                            }
                          }}
                        />
                        
                        {/* Design File Section */}
                        <div style={{ 
                          marginTop: '12px', 
                          padding: '12px', 
                          background: item.design_file ? '#f0fdf4' : '#fafafa',
                          borderRadius: '8px',
                          border: item.design_file ? '1px solid #86efac' : '1px dashed #d1d5db'
                        }}>
                          {item.design_file ? (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                {item.design_type?.startsWith('image/') ? (
                                  <img 
                                    src={item.design_file} 
                                    alt="Design" 
                                    style={{ 
                                      width: '60px', 
                                      height: '60px', 
                                      objectFit: 'contain',
                                      borderRadius: '4px',
                                      border: '1px solid #e2e8f0'
                                    }}
                                  />
                                ) : (
                                  <div style={{
                                    width: '60px',
                                    height: '60px',
                                    background: '#fee2e2',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '10px',
                                    fontWeight: '600',
                                    color: '#dc2626'
                                  }}>
                                    PDF
                                  </div>
                                )}
                                <div>
                                  <p style={{ fontSize: '13px', fontWeight: '600', color: '#166534' }}>Design Attached</p>
                                  <p style={{ fontSize: '11px', color: '#6b7280' }}>{item.design_filename}</p>
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <a 
                                  href={item.design_file} 
                                  download={item.design_filename}
                                  style={{
                                    padding: '4px 10px',
                                    fontSize: '11px',
                                    background: '#3b82f6',
                                    color: 'white',
                                    borderRadius: '4px',
                                    textDecoration: 'none'
                                  }}
                                >
                                  Download
                                </a>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => removeItemDesign(idx)}
                                  style={{ padding: '4px 8px', fontSize: '11px', color: '#ef4444', borderColor: '#ef4444' }}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <p style={{ fontSize: '12px', color: '#6b7280' }}>No design file attached</p>
                                <p style={{ fontSize: '11px', color: '#9ca3af' }}>Upload JPEG, PNG, or PDF</p>
                              </div>
                              <label style={{ cursor: 'pointer' }}>
                                <input
                                  type="file"
                                  accept=".jpg,.jpeg,.png,.pdf"
                                  onChange={(e) => handleItemDesignUpload(idx, e.target.files[0])}
                                  style={{ display: 'none' }}
                                  data-testid={`upload-design-${idx}`}
                                />
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={uploadingItemDesign === idx}
                                  style={{ 
                                    padding: '4px 12px', 
                                    fontSize: '12px',
                                    background: '#667eea',
                                    color: 'white',
                                    border: 'none',
                                    pointerEvents: 'none'
                                  }}
                                >
                                  <Upload className="w-3 h-3 mr-1" />
                                  {uploadingItemDesign === idx ? 'Uploading...' : 'Upload Design'}
                                </Button>
                              </label>
                            </div>
                          )}
                        </div>

                        {/* Mockup Designer & Approval Section */}
                        <div style={{ 
                          marginTop: '12px', 
                          padding: '12px', 
                          background: '#f0f4ff',
                          borderRadius: '8px',
                          border: '1px solid #c7d2fe'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Wand2 className="w-4 h-4" style={{ color: '#667eea' }} />
                              <div>
                                <p style={{ fontSize: '12px', fontWeight: '600', color: '#4338ca' }}>Mockup Designer</p>
                                <p style={{ fontSize: '11px', color: '#6366f1' }}>Create & send for approval</p>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              {/* Approval Status Badge */}
                              {item.approval_status && (
                                <span style={{
                                  padding: '4px 10px',
                                  borderRadius: '9999px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  background: item.approval_status === 'approved' ? '#dcfce7' : 
                                              item.approval_status === 'changes_requested' ? '#fef3c7' : '#dbeafe',
                                  color: item.approval_status === 'approved' ? '#166534' : 
                                         item.approval_status === 'changes_requested' ? '#92400e' : '#1e40af',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}>
                                  {item.approval_status === 'approved' && <Check className="w-3 h-3" />}
                                  {item.approval_status === 'changes_requested' && <MessageSquare className="w-3 h-3" />}
                                  {item.approval_status === 'approved' ? 'Approved' : 
                                   item.approval_status === 'changes_requested' ? 'Changes Requested' : 'Pending'}
                                </span>
                              )}
                              <Button
                                size="sm"
                                onClick={() => navigate(`/mockup-designer?proposal=${id}&item=${idx}`)}
                                style={{ 
                                  padding: '6px 14px', 
                                  fontSize: '12px',
                                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                  color: 'white',
                                  border: 'none'
                                }}
                                data-testid={`mockup-designer-${idx}`}
                              >
                                <Wand2 className="w-3 h-3 mr-1" />
                                Open Designer
                              </Button>
                            </div>
                          </div>
                          
                          {/* Show feedback if changes requested */}
                          {item.approval_status === 'changes_requested' && item.approval_feedback && (
                            <div style={{ 
                              marginTop: '8px', 
                              padding: '8px 12px', 
                              background: '#fef3c7', 
                              borderRadius: '6px',
                              fontSize: '12px',
                              color: '#92400e'
                            }}>
                              <strong>Customer Feedback:</strong> {item.approval_feedback}
                            </div>
                          )}

                          {/* Full audit trail: every sent / change-request / approval with timestamps + locked design snapshot */}
                          <div style={{ marginTop: 12, padding: '10px 12px', background: 'white', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                            <MockupApprovalTimeline proposalId={id} itemIndex={idx} />
                          </div>
                          
                          {/* Show mockup preview if exists */}
                          {item.mockup_url && (
                            <div style={{ marginTop: '8px' }}>
                              <img 
                                src={item.mockup_url} 
                                alt="Mockup" 
                                style={{ 
                                  maxWidth: '150px', 
                                  maxHeight: '100px', 
                                  objectFit: 'contain',
                                  borderRadius: '6px',
                                  border: '1px solid #c7d2fe'
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                      );
                    })}
                  </div>
                ))}
                
                {/* Pricing Summary */}
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '2px solid #e2e8f0' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '12px' }}>
                    <div>
                      <p style={{ fontSize: '12px', color: '#718096' }}>Garments</p>
                      <p style={{ fontWeight: '600', fontSize: '16px' }}>${(proposal.shirt_total || 0).toFixed(2)}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '12px', color: '#718096' }}>Decoration</p>
                      <p style={{ fontWeight: '600', fontSize: '16px' }}>${(proposal.print_total || 0).toFixed(2)}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '12px', color: '#718096' }}>Setup Fees</p>
                      <p style={{ fontWeight: '600', fontSize: '16px' }}>${(proposal.setup_fees || 0).toFixed(2)}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '12px', color: '#718096' }}>
                        Tax {proposal.tax_exempt && <span style={{ fontSize: '10px', color: '#f59e0b' }}>(Exempt)</span>}
                      </p>
                      <p style={{ fontWeight: '600', fontSize: '16px' }}>${(proposal.tax_amount || 0).toFixed(2)}</p>
                    </div>
                    <div style={{ borderLeft: '2px solid #667eea', paddingLeft: '12px' }}>
                      <p style={{ fontSize: '12px', color: '#718096' }}>Grand Total</p>
                      <p style={{ fontWeight: '700', fontSize: '20px', color: '#22c55e' }}>${(proposal.total_amount || 0).toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Payments History */}
            <PaymentsSection
              proposal={proposal}
              onRefunded={(updated) => setProposal(updated)}
            />

            {/* Shirt Mockup Views - Legacy proposal-level preview.
                Hidden whenever the per-item "Product Mockups" section below
                will render (i.e. items.length > 0) — that section is the
                modern, per-item, 4-view source of truth and showing both at
                once made old proposals (like order 1013000) look inconsistent
                with newer ones. */}
            {(!proposal.items || proposal.items.length === 0) && proposal.mockup_views && Object.keys(proposal.mockup_views).length > 0 ? (
              <div style={{ marginBottom: '32px', background: 'white', padding: '24px', borderRadius: '12px', border: '2px solid #667eea' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#667eea' }}>
                    📸 Shirt Mockup Preview
                  </h3>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setShowMockupEditor(!showMockupEditor);
                      // Initialize with current values
                      if (proposal.garment_type) setSelectedGarmentType(proposal.garment_type);
                      if (proposal.items?.[0]?.color) setSelectedColor(proposal.items[0].color);
                    }}
                    data-testid="edit-mockup-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Edit Mockup
                  </Button>
                </div>
                <p style={{ color: '#718096', fontSize: '14px', marginBottom: '20px' }}>
                  Professional mockup of your custom {proposal.garment_type || 'apparel'} with logo placement
                </p>
                
                {/* Mockup Editor Panel */}
                {showMockupEditor && (
                  <div style={{ 
                    background: '#f0f4ff', 
                    padding: '20px', 
                    borderRadius: '12px', 
                    marginBottom: '20px',
                    border: '1px solid #667eea'
                  }}>
                    <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#4a5568' }}>
                      🎨 Mockup Editor
                    </h4>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                      {/* Garment Type */}
                      <div>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px', color: '#4a5568' }}>
                          Garment Type
                        </label>
                        <Select value={selectedGarmentType} onValueChange={setSelectedGarmentType}>
                          <SelectTrigger data-testid="garment-type-select">
                            <SelectValue placeholder="Select garment type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="t-shirt">T-Shirt</SelectItem>
                            <SelectItem value="polo">Polo Shirt</SelectItem>
                            <SelectItem value="hoodie">Hoodie</SelectItem>
                            <SelectItem value="long_sleeve">Long Sleeve</SelectItem>
                            <SelectItem value="tank_top">Tank Top</SelectItem>
                            <SelectItem value="jacket">Jacket</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Color Override */}
                      <div>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px', color: '#4a5568' }}>
                          Shirt Color
                        </label>
                        <Select value={selectedColor} onValueChange={setSelectedColor}>
                          <SelectTrigger data-testid="color-select">
                            <SelectValue placeholder="Keep current color" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="keep-current">Keep Current</SelectItem>
                            <SelectItem value="Black">Black</SelectItem>
                            <SelectItem value="White">White</SelectItem>
                            <SelectItem value="Navy">Navy</SelectItem>
                            <SelectItem value="Royal Blue">Royal Blue</SelectItem>
                            <SelectItem value="Red">Red</SelectItem>
                            <SelectItem value="Charcoal">Charcoal</SelectItem>
                            <SelectItem value="Heather Gray">Heather Gray</SelectItem>
                            <SelectItem value="Forest Green">Forest Green</SelectItem>
                            <SelectItem value="Maroon">Maroon</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    {/* Logo Upload */}
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px', color: '#4a5568' }}>
                        Upload New Logo (Optional)
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          style={{ display: 'none' }}
                          id="logo-upload"
                          data-testid="logo-upload-input"
                        />
                        <label 
                          htmlFor="logo-upload"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '8px 16px',
                            background: 'white',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {newLogoFile ? newLogoFile.name : 'Choose Logo File'}
                        </label>
                        {newLogoPreview && (
                          <img 
                            src={`data:image/png;base64,${newLogoPreview}`} 
                            alt="Logo preview" 
                            style={{ height: '40px', borderRadius: '4px' }}
                          />
                        )}
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <Button 
                        onClick={regenerateMockups} 
                        disabled={regeneratingMockup}
                        data-testid="regenerate-mockup-btn"
                      >
                        {regeneratingMockup ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Regenerating...
                          </>
                        ) : (
                          <>
                            <Palette className="w-4 h-4 mr-2" />
                            Regenerate Mockup
                          </>
                        )}
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => setShowMockupEditor(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Logo Placement Editor Modal */}
                {showLogoPlacement && proposal.mockup_views?.[selectedView] && (
                  <div style={{ marginBottom: '20px' }}>
                    {!newLogoPreview ? (
                      <div style={{ 
                        background: '#f0f4ff', 
                        padding: '24px', 
                        borderRadius: '12px',
                        border: '2px dashed #667eea',
                        textAlign: 'center'
                      }}>
                        <h4 style={{ color: '#4a5568', marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>
                          Step 1: Upload Your Logo
                        </h4>
                        <p style={{ color: '#718096', marginBottom: '16px', fontSize: '14px' }}>
                          Select a logo image to position on the {selectedView} mockup
                        </p>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setNewLogoFile(file);
                              const reader = new FileReader();
                              reader.onload = () => {
                                const base64 = reader.result.split(',')[1];
                                setNewLogoPreview(base64);
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          style={{ display: 'none' }}
                          id={`logo-placement-upload-${selectedView}`}
                          key={`logo-input-${selectedView}`}
                        />
                        <label 
                          htmlFor={`logo-placement-upload-${selectedView}`}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '12px 24px',
                            background: '#667eea',
                            color: 'white',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Choose Logo File
                        </label>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setShowLogoPlacement(false)}
                          style={{ marginLeft: '12px' }}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <div style={{ 
                        background: '#1a1a2e', 
                        padding: '20px', 
                        borderRadius: '12px',
                        border: '2px solid #667eea'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <h4 style={{ color: 'white', fontSize: '16px', fontWeight: '600', margin: 0 }}>
                            Step 2: Position Your Logo
                          </h4>
                          <span style={{ color: '#a0aec0', fontSize: '12px' }}>
                            Drag to move • Corners to resize
                          </span>
                        </div>
                        <LogoPlacementEditor
                          mockupBase64={proposal.base_mockup_views?.[selectedView] || proposal.mockup_views[selectedView]}
                          logoBase64={newLogoPreview}
                          initialPosition={proposal.logo_positions?.[selectedView] || { x: 50, y: 35, width: 35, height: 35, rotation: 0 }}
                          onSave={applyLogoPosition}
                          onCancel={() => {
                            setShowLogoPlacement(false);
                            setNewLogoFile(null);
                            setNewLogoPreview(null);
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
                  {Object.entries(proposal.mockup_views).map(([view, imageBase64]) => (
                    <div key={view} style={{ 
                      textAlign: 'center',
                      background: '#f7fafc',
                      padding: '16px',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      position: 'relative'
                    }}>
                      <img
                        src={`data:image/png;base64,${imageBase64}`}
                        alt={`${view} view`}
                        style={{ 
                          width: '100%', 
                          maxWidth: '280px',
                          height: 'auto',
                          borderRadius: '8px', 
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          border: '3px solid white',
                          marginBottom: '12px'
                        }}
                        data-testid={`mockup-${view}`}
                      />
                      <p style={{ 
                        fontSize: '14px', 
                        fontWeight: '600', 
                        color: '#4a5568', 
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: '8px'
                      }}>
                        {view.replace('_', ' ')}
                      </p>
                      {/* Add Logo Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openLogoPlacement(view)}
                        style={{ fontSize: '12px' }}
                        data-testid={`add-logo-${view}-btn`}
                      >
                        <Move className="w-3 h-3 mr-1" />
                        Position Logo
                      </Button>
                      {/* Add Text Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openTextEditor(view)}
                        style={{ fontSize: '12px', marginLeft: '8px' }}
                        data-testid={`add-text-${view}-btn`}
                      >
                        <Type className="w-3 h-3 mr-1" />
                        Add Text
                      </Button>
                    </div>
                  ))}
                </div>
                
                {/* Text Overlay Editor */}
                {showTextEditor && proposal.mockup_views?.[selectedView] && (
                  <div style={{ marginTop: '20px' }}>
                    <TextOverlayEditor
                      mockupBase64={proposal.mockup_views[selectedView]}
                      initialPosition={proposal.text_overlays?.[selectedView]?.position || { x: 50, y: 35, width: 40, height: 15, rotation: 0 }}
                      onSave={applyTextOverlay}
                      onCancel={() => setShowTextEditor(false)}
                    />
                  </div>
                )}
              </div>
            ) : null}

            {/* Per-Product Mockups Section */}
            {proposal.items && proposal.items.length > 0 && (
              <div data-testid="product-mockups-section" style={{ marginBottom: '32px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
                    📦 Product Mockups
                  </h3>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Button size="sm" variant="outline" onClick={approveAllMockups}
                      data-testid="approve-all-mockups-btn"
                      style={{ borderColor: '#16a34a', color: '#16a34a' }}>
                      <Check size={14} style={{ marginRight: 6 }} /> Approve All Mockups
                    </Button>
                    <Button size="sm" variant="outline" onClick={downloadProductionPack}
                      data-testid="production-pack-section-btn"
                      style={{ borderColor: '#7c3aed', color: '#7c3aed' }}>
                      <Download size={14} style={{ marginRight: 6 }} /> Production Pack PDF
                    </Button>
                  </div>
                </div>
                <p style={{ color: '#718096', fontSize: '14px', marginBottom: '16px' }}>
                  Each product can have its own logo and mockup. Click a product to expand and edit.
                </p>
                {proposal.items.map((item, idx) => (
                  <ProductMockupCard
                    key={item.id || idx}
                    item={item}
                    itemIndex={idx}
                    proposalId={id}
                    customerEmail={proposal.customer_email}
                    onUpdate={handleItemUpdate}
                  />
                ))}
              </div>
            )}

            <PrintSpecificationsEditor
              proposal={proposal}
              onSaved={(updated) => setProposal(updated)}
            />
          </div>

          {/* Pricing Breakdown */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
              Pricing Breakdown
              <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400, marginLeft: 10, letterSpacing: 0.3 }}>
                · click any line to override
              </span>
            </h3>
            <div style={{ background: '#f7fafc', padding: '24px', borderRadius: '12px' }}>
              {[
                { key: 'shirt_total', label: 'Shirt Total' },
                { key: 'print_total', label: 'Print Total' },
                { key: 'setup_fees', label: 'Setup Fees' },
                { key: 'additional_fees', label: 'Additional Fees' },
              ].map(({ key, label }) => (
                <EditableBreakdownRow
                  key={key}
                  label={label}
                  value={Number(proposal[key] || 0)}
                  testId={`pricing-${key.replace('_total', '').replace('_fees', '').replace('_fee', '')}`}
                  editable={user?.role === 'admin'}
                  onSave={async (newVal) => {
                    try {
                      const { data } = await axios.patch(`/proposals/${id}/breakdown`, { [key]: newVal });
                      setProposal(data.proposal || data);
                      toast.success(`${label} set to $${Number(newVal).toFixed(2)}`);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Failed to update');
                    }
                  }}
                />
              ))}
              
              {/* Subtotal before tax */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', paddingTop: '12px', borderTop: '1px dashed #cbd5e0' }}>
                <span style={{ fontWeight: '600' }}>Subtotal:</span>
                <span style={{ fontWeight: '600' }} data-testid="pricing-subtotal">
                  ${(proposal.subtotal || ((proposal.shirt_total || 0) + (proposal.print_total || 0) + (proposal.setup_fees || 0) + (proposal.additional_fees || 0))).toFixed(2)}
                </span>
              </div>
              
              {/* Tax Toggle and Display */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '12px',
                padding: '12px',
                background: proposal.tax_exempt ? '#fef3c7' : '#f0fdf4',
                borderRadius: '8px',
                border: `1px solid ${proposal.tax_exempt ? '#fbbf24' : '#86efac'}`
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span>Florida Sales Tax (7%):</span>
                  <button
                    onClick={toggleTaxExempt}
                    disabled={updatingTax || proposal.status === 'paid'}
                    style={{
                      padding: '4px 12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      borderRadius: '9999px',
                      border: 'none',
                      cursor: proposal.status === 'paid' ? 'not-allowed' : 'pointer',
                      background: proposal.tax_exempt ? '#f59e0b' : '#22c55e',
                      color: 'white',
                      opacity: updatingTax ? 0.6 : 1
                    }}
                    data-testid="tax-exempt-toggle"
                  >
                    {updatingTax ? 'Updating...' : (proposal.tax_exempt ? 'Tax Exempt' : 'Taxable')}
                  </button>
                  {proposal.tax_inherited_from_customer && (
                    <span style={{ 
                      fontSize: '11px', 
                      color: '#92400e', 
                      background: '#fef3c7', 
                      padding: '2px 8px', 
                      borderRadius: '4px' 
                    }}>
                      Inherited from customer
                    </span>
                  )}
                </div>
                <span style={{ fontWeight: '500' }} data-testid="pricing-tax">
                  {proposal.tax_exempt ? '$0.00' : `$${(proposal.tax_amount || 0).toFixed(2)}`}
                </span>
              </div>
              
              {/* Tax Certificate Section - Only show when tax exempt */}
              {proposal.tax_exempt && (
                <div style={{ 
                  marginBottom: '12px',
                  padding: '16px',
                  background: '#fffbeb',
                  borderRadius: '8px',
                  border: '1px solid #fbbf24'
                }}>
                  {proposal.tax_certificate ? (
                    // Show uploaded certificate
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontWeight: '600', color: '#92400e' }}>
                          Tax Certificate on File
                          {proposal.tax_inherited_from_customer && (
                            <span style={{ fontWeight: '400', fontSize: '11px', marginLeft: '8px' }}>(from customer record)</span>
                          )}
                        </span>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <a 
                            href={proposal.tax_certificate.data} 
                            download={proposal.tax_certificate.filename}
                            style={{
                              padding: '4px 10px',
                              fontSize: '12px',
                              background: '#3b82f6',
                              color: 'white',
                              borderRadius: '4px',
                              textDecoration: 'none'
                            }}
                          >
                            Download
                          </a>
                          <button
                            onClick={deleteTaxCertificate}
                            style={{
                              padding: '4px 10px',
                              fontSize: '12px',
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                            data-testid="delete-certificate-btn"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                      <div style={{ fontSize: '13px', color: '#78350f' }}>
                        <p><strong>File:</strong> {proposal.tax_certificate.filename}</p>
                        {proposal.tax_certificate.certificate_number && (
                          <p><strong>Certificate #:</strong> {proposal.tax_certificate.certificate_number}</p>
                        )}
                        {proposal.tax_certificate.expiry_date && (
                          <p><strong>Expires:</strong> {new Date(proposal.tax_certificate.expiry_date).toLocaleDateString()}</p>
                        )}
                        <p style={{ fontSize: '11px', color: '#a16207', marginTop: '4px' }}>
                          Uploaded by {proposal.tax_certificate.uploaded_by} on {new Date(proposal.tax_certificate.uploaded_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ) : showCertificateUpload ? (
                    // Show certificate upload form
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <span style={{ fontWeight: '600', color: '#92400e' }}>Upload Resale Certificate</span>
                        <button
                          onClick={() => setShowCertificateUpload(false)}
                          style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '18px',
                            cursor: 'pointer',
                            color: '#92400e'
                          }}
                        >
                          ×
                        </button>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: '#78350f' }}>
                            Certificate File (PDF, PNG, JPG) *
                          </label>
                          <input
                            type="file"
                            accept=".pdf,.png,.jpg,.jpeg"
                            onChange={handleCertificateFileChange}
                            style={{
                              width: '100%',
                              padding: '8px',
                              borderRadius: '4px',
                              border: '1px solid #d4a373',
                              background: 'white',
                              fontSize: '13px'
                            }}
                            data-testid="certificate-file-input"
                          />
                          {certificateFile && (
                            <p style={{ fontSize: '11px', color: '#22c55e', marginTop: '4px' }}>
                              Selected: {certificateFile.name}
                            </p>
                          )}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                          <div>
                            <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: '#78350f' }}>
                              Certificate Number (Optional)
                            </label>
                            <input
                              type="text"
                              value={certificateNumber}
                              onChange={(e) => setCertificateNumber(e.target.value)}
                              placeholder="e.g., 12-345678-9"
                              style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #d4a373',
                                fontSize: '13px'
                              }}
                              data-testid="certificate-number-input"
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: '#78350f' }}>
                              Expiry Date (Optional)
                            </label>
                            <input
                              type="date"
                              value={certificateExpiry}
                              onChange={(e) => setCertificateExpiry(e.target.value)}
                              style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #d4a373',
                                fontSize: '13px'
                              }}
                              data-testid="certificate-expiry-input"
                            />
                          </div>
                        </div>
                        <Button
                          onClick={uploadTaxCertificate}
                          disabled={uploadingCertificate || !certificateFile}
                          style={{
                            background: '#f59e0b',
                            color: 'white',
                            border: 'none'
                          }}
                          data-testid="upload-certificate-btn"
                        >
                          {uploadingCertificate ? 'Uploading...' : 'Upload Certificate'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // Show prompt to add certificate
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span style={{ fontWeight: '600', color: '#92400e' }}>No Tax Certificate</span>
                        <p style={{ fontSize: '12px', color: '#78350f', marginTop: '2px' }}>
                          Upload a resale certificate for compliance records
                        </p>
                      </div>
                      <Button
                        onClick={() => setShowCertificateUpload(true)}
                        size="sm"
                        style={{
                          background: '#f59e0b',
                          color: 'white',
                          border: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                        data-testid="add-certificate-btn"
                      >
                        <Upload size={14} />
                        Add Certificate
                      </Button>
                    </div>
                  )}
                </div>
              )}
              
              {/* Total Amount */}
              <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '2px solid #667eea', paddingTop: '16px', fontSize: '20px', fontWeight: '700' }}>
                <span>Total Amount:</span>
                <span style={{ color: '#667eea' }} data-testid="pricing-total">${(proposal.total_amount || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {proposal.notes && (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>Notes</h3>
              <div style={{ background: '#f7fafc', padding: '16px', borderRadius: '8px' }}>
                <p data-testid="proposal-notes">{proposal.notes}</p>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Production Pack Preview / Print Modal */}
      {previewPdfUrl && (
        <div
          data-testid="production-pack-preview-modal"
          onClick={closePreview}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(15, 23, 42, 0.75)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '24px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: '14px',
              width: 'min(1100px, 100%)', height: 'min(92vh, 100%)',
              display: 'flex', flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0,0,0,0.45)',
              overflow: 'hidden',
            }}
          >
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 20px', borderBottom: '2px solid #e2e8f0',
              background: 'linear-gradient(180deg, #fafafa, #f1f5f9)',
            }}>
              <div>
                <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 600, letterSpacing: 0.4 }}>PRODUCTION PACK PREVIEW</div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', marginTop: 2 }}>
                  Order #{proposal.proposal_number} · {proposal.customer_name}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button onClick={printProductionPack} data-testid="print-production-pack-button"
                  style={{ background: '#7c3aed', color: 'white' }}>
                  <Printer size={16} style={{ marginRight: 6 }} /> Print
                </Button>
                <Button variant="outline" onClick={downloadFromPreview} data-testid="preview-download-button">
                  <Download size={16} style={{ marginRight: 6 }} /> Download
                </Button>
                <Button variant="outline" onClick={closePreview} data-testid="close-preview-button">
                  <X size={16} />
                </Button>
              </div>
            </div>
            <iframe
              id="production-pack-preview-iframe"
              title="Production Pack Preview"
              src={previewPdfUrl}
              style={{ flex: 1, border: 'none', width: '100%' }}
            />
          </div>
        </div>
      )}
      {modalItemIndex !== null && proposal?.items?.[modalItemIndex] && (
        <EditProductModal
          proposalId={id}
          itemIndex={modalItemIndex}
          item={proposal.items[modalItemIndex]}
          taxRate={proposal.tax_rate || 0}
          onClose={() => setModalItemIndex(null)}
          onSaved={(updated) => setProposal(updated)}
        />
      )}

      {/* Pickup receipt modal — admin opens it when the customer arrives,
          customer signs on the tablet, submit flips status to picked_up. */}
      {pickupOpen && (
        <PickupReceipt
          open={pickupOpen}
          onClose={() => setPickupOpen(false)}
          proposal={proposal}
          currentUser={user}
          onSaved={() => fetchProposal()}
        />
      )}

      {/* In-office payment modal — admin records cash/check/other on the spot
          OR launches the public Collect.js card terminal in a new tab. */}
      {showAdminPayment && (
        <AdminPaymentModal
          proposal={proposal}
          onClose={() => setShowAdminPayment(false)}
          onPaymentRecorded={() => fetchProposal()}
        />
      )}
    </div>
  );
};

export default ProposalView;

// Inline-editable money cell for the Pricing Breakdown rows. Click the
// amount to swap into an input; press Enter or click Save to commit.
const EditableBreakdownRow = ({ label, value, testId, editable, onSave }) => {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
      <span>{label}:</span>
      {editing ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontWeight: 500 }}>$</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={draft}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                document.querySelector(`[data-testid="save-${testId}"]`)?.click();
              } else if (e.key === 'Escape') {
                setEditing(false);
              }
            }}
            data-testid={`input-${testId}`}
            style={{
              width: 100, padding: '4px 8px',
              border: '1px solid #cbd5e1', borderRadius: 6,
              fontSize: 14, fontWeight: 500, color: '#0f172a', textAlign: 'right',
            }}
          />
          <button
            onClick={async () => {
              const v = parseFloat(draft);
              if (!Number.isFinite(v) || v < 0) return;
              setSaving(true);
              try {
                await onSave(v);
                setEditing(false);
              } finally {
                setSaving(false);
              }
            }}
            disabled={saving}
            data-testid={`save-${testId}`}
            style={{
              padding: '4px 10px', background: '#0ea5e9', color: '#fff',
              border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {saving ? '…' : 'Save'}
          </button>
          <button
            onClick={() => setEditing(false)}
            data-testid={`cancel-${testId}`}
            style={{
              padding: '4px 8px', background: 'transparent', color: '#64748b',
              border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12,
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        </span>
      ) : (
        <button
          onClick={() => {
            if (!editable) return;
            setDraft(value.toFixed(2));
            setEditing(true);
          }}
          disabled={!editable}
          data-testid={testId}
          title={editable ? 'Click to override' : ''}
          style={{
            background: 'transparent', border: 'none', padding: '2px 4px',
            cursor: editable ? 'pointer' : 'default',
            fontWeight: 500, fontSize: 15, color: '#0f172a',
            borderBottom: editable ? '1px dashed #94a3b8' : 'none',
          }}
        >
          ${Number(value || 0).toFixed(2)}
        </button>
      )}
    </div>
  );
};
